import { useRef } from 'react';
import { HOME, type Figure } from '@/data/home';
import {
    CountUp,
    clamp,
    lerp,
    prefersReducedMotion,
    useInView,
    useMotion,
    useViewportProgress,
} from '@components/motion';
import FigmaFrame from './FigmaFrame';

// Scroll-scrub colour ramp for the mobile heading tail: grey → black.
const GREY: [number, number, number] = [199, 199, 199];
const BLACK: [number, number, number] = [27, 27, 27];
const mixColor = (a: [number, number, number], b: [number, number, number], t: number) =>
    `rgb(${Math.round(lerp(a[0], b[0], t))}, ${Math.round(lerp(a[1], b[1], t))}, ${Math.round(lerp(a[2], b[2], t))})`;

/** One repeat of the marquee: the full statement + an orange bullet spacer. */
function MarqueeUnit({ hidden = false }: { hidden?: boolean }) {
    return (
        <span className="flex items-center" aria-hidden={hidden || undefined}>
            <span>{HOME.metrics.heading}</span>
            <span className="px-[70px] text-[#ff6600]">•</span>
        </span>
    );
}

type Metric = {
    /** Optional faint leading character (e.g. the grey "0" in "09k"). */
    dim?: string;
    /** Decimal places the figure was written with — 6.5k must not count up to 7k. */
    decimals: number;
    value: string;
    /** Numeric target + unit for the count-up. */
    end: number;
    suffix: string;
    label: string;
};

/**
 * A figure as the CMS writes it ("10k", "09k", "20M", "6,500+") split into what
 * the count-up needs: an optional faint leading zero, the number, and the unit
 * that follows it. Anything unparseable counts up from zero to its own text.
 */
function parseFigure(f: Figure): Metric {
    const m = /^(0*)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(f.value.trim());
    if (!m) return { value: f.value, end: 0, decimals: 0, suffix: f.value, label: f.label };
    const [, zeros, digits, suffix] = m;
    return {
        dim: zeros || undefined,
        value: `${digits}${suffix}`,
        end: Number(digits.replace(/,/g, '')),
        decimals: digits.split('.')[1]?.length ?? 0,
        suffix: suffix.trim(),
        label: f.label,
    };
}

/** Read at render so the hydrated figures are what count up. */
const metrics = (): Metric[] => HOME.metrics.figures.map(parseFigure);

/* Small grey track marker: outer #E0E0E0 disc with a #1B1B1B centre dot. */
const Dot = ({ className }: { className: string }) => (
    <div className={`absolute size-[41px] rounded-full bg-[#e0e0e0] ${className}`}>
        <div className="absolute left-1/2 top-1/2 size-[8px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1b1b1b]" />
    </div>
);

/**
 * White handle disc with a hairline ring. On entry it slides across the bar from
 * the track dot to its resting position (`slideFrom` = negative of its resting
 * `left`), staggered per row via `delay`.
 */
const Handle = ({ className, slideFrom, delay = 0 }: { className: string; slideFrom: number; delay?: number }) => (
    <div
        className={`cg-knob absolute h-[102px] w-[103px] rounded-full bg-white ${className}`}
        style={
            {
                'boxShadow': 'inset 0 0 0 1px rgba(0,0,0,0.04)',
                'transitionDelay': `${delay}s`,
                '--cg-knob-from': `${slideFrom}px`,
            } as React.CSSProperties
        }
    />
);

/**
 * A pill track: the coloured gradient fill (kept at its true tablet-view colour —
 * no luminosity blend) with a soft light shimmer sweeping across it on a loop.
 */
const Bar = ({ rounded, gradient, delay }: { rounded: string; gradient: string; delay: number }) => (
    <div className={`absolute left-[98px] top-0 h-[132px] w-[1280px] overflow-hidden ${rounded}`}>
        <div className="absolute inset-0 opacity-[0.49]" style={{ backgroundImage: gradient }} />
        <span className="cg-shimmer" style={{ animationDelay: `${delay}s` }} />
    </div>
);

/** Bar, handle and gradient per designed row — grey, green, orange. */
const DESKTOP_ROWS = [
    {
        top: 0,
        dot: 'left-0 top-[60px]',
        rounded: 'rounded-[66px]',
        gradient: 'linear-gradient(90deg, #d9d9d9 0%, rgba(128,128,128,0.13) 67.02%, rgba(115,115,115,0) 100%)',
        barDelay: 0,
        handle: 'left-[118px] top-[16px]',
        slideFrom: -118,
    },
    {
        top: 260,
        dot: 'left-0 top-[47px]',
        rounded: 'rounded-[145px]',
        // Mirrored: fades from transparent (left) into green (right).
        gradient: 'linear-gradient(270deg, #dae2c9 0%, #dae2c9 67.02%, #f8f8f6 100%)',
        barDelay: 0.6,
        handle: 'left-[1255px] top-[16px]',
        slideFrom: -1255,
    },
    {
        top: 520,
        dot: 'left-0 top-[49px]',
        rounded: 'rounded-[145px]',
        gradient: 'linear-gradient(90deg, #ffd9bf 0%, rgba(255,217,191,0) 100%)',
        barDelay: 1.2,
        handle: 'left-[118px] top-[16px]',
        slideFrom: -118,
    },
];

function ImpactMetricsDesktop({ rows }: { rows: Metric[] }) {
    const ref = useRef<HTMLDivElement>(null);
    const active = useInView(ref, { threshold: 0.25 });

    return (
        <FigmaFrame designWidth={1920} designHeight={1101} className="w-full">
            <div
                ref={ref}
                data-mxin={active ? 'true' : undefined}
                className="relative h-[1101px] w-[1920px] bg-white text-[#1b1b1b]"
            >
                {/* Heading — a continuous edge-to-edge marquee that loops nonstop.
                    The mask is tall enough (and vertically centred on the original
                    baseline) that descenders like y / g / , are never clipped. */}
                <div className="absolute left-0 top-[62px] flex h-[244px] w-[1920px] items-center overflow-hidden">
                    <div className="cg-marquee flex w-max shrink-0 items-center whitespace-nowrap font-['Plus_Jakarta_Sans'] text-[150px] font-medium leading-[144px] text-[#c7c7c7] will-change-transform">
                        <MarqueeUnit />
                        <MarqueeUnit hidden />
                    </div>
                </div>

                {/* Metrics block */}
                <div className="absolute left-[99px] top-[341px] h-[652px] w-[1684px]">
                    {/* The three designed rows, each with its own bar and handle; a figure fills each in turn. */}
                    {rows.map((m, i) => {
                        const style = DESKTOP_ROWS[i];
                        return (
                            <div
                                key={m.label}
                                className="absolute left-0 h-[132px] w-[1683.5px]"
                                style={{ top: style.top }}
                            >
                                <div
                                    className="cg-num absolute left-0 top-1/2 flex -translate-y-1/2 flex-col gap-[4px]"
                                    style={{ transitionDelay: `${i * 0.12}s` }}
                                >
                                    <p className="text-[100px] leading-[0.9]">
                                        {m.dim && <span className="text-[#cfcfcf]">{m.dim}</span>}
                                        <CountUp end={m.end} decimals={m.decimals} suffix={m.suffix} />
                                    </p>
                                    <p className="text-[20px] leading-[32px]">{m.label}</p>
                                </div>
                                <div className="absolute left-[305px] top-0 h-[132px] w-[1378px]">
                                    <Dot className={style.dot} />
                                    <Bar rounded={style.rounded} gradient={style.gradient} delay={style.barDelay} />
                                    <Handle className={style.handle} slideFrom={style.slideFrom} delay={i * 0.12} />
                                </div>
                            </div>
                        );
                    })}

                    {/* Dividers */}
                    {rows.length > 1 && (
                        <div className="absolute left-0 top-[195px] h-[7px] w-[1683.5px]">
                            <div className="h-full w-full rounded-full bg-[#f9f9f9]" />
                        </div>
                    )}
                    {rows.length > 2 && (
                        <div className="absolute left-0 top-[455px] h-[7px] w-[1683.5px]">
                            <div className="h-full w-full rounded-full bg-[#f9f9f9]" />
                        </div>
                    )}
                </div>
            </div>
        </FigmaFrame>
    );
}

/* Mobile metric row config: label + the full-width pill track styling. */
type MobileBar = {
    /** Gradient applied to the pill track. */
    background: string;
    /** Pill corner radius / extra track classes. */
    barClassName?: string;
    /** Handle horizontal position within the track. */
    handle: 'left' | 'right';
};

const MOBILE_BARS: MobileBar[] = [
    {
        // Row 1 — 10k: handle left, grey fade left → transparent right.
        background: 'linear-gradient(90deg, #d9d9d9 0%, rgba(128,128,128,0.13) 67.02%, rgba(115,115,115,0) 100%)',
        handle: 'left',
    },
    {
        // Row 2 — 09k: handle right, green fade right → transparent left.
        background: 'linear-gradient(270deg, #dae2c9 0%, #dae2c9 67.02%, #f8f8f6 100%)',
        handle: 'right',
    },
    {
        // Row 3 — 20M: handle left, orange fade left → transparent right.
        background: 'linear-gradient(90deg, #ffd9bf 0%, rgba(255,217,191,0) 100%)',
        handle: 'left',
    },
];

function ImpactMetricsMobile({ rows }: { rows: Metric[] }) {
    const sectionRef = useRef<HTMLElement>(null);
    const greyRef = useRef<HTMLSpanElement>(null);
    const progress = useViewportProgress(sectionRef);

    // The grey tail of the heading inks in to black as the section scrolls past.
    useMotion(greyRef, progress, (el, p) => {
        if (prefersReducedMotion()) return;
        el.style.color = mixColor(GREY, BLACK, clamp((p - 0.1) / 0.45));
    });

    return (
        <section ref={sectionRef} className="bg-white px-5 py-14 text-[#1b1b1b]">
            <h2 className="font-['Plus_Jakarta_Sans'] text-[40px] font-medium leading-[1.4]">
                <span>{HOME.metrics.heading.slice(0, 10)}</span>
                <span ref={greyRef} className="text-[#c7c7c7]">
                    {HOME.metrics.heading.slice(10)}
                </span>
            </h2>

            <div className="mt-12 flex flex-col">
                {rows.map((m, i) => {
                    const bar = MOBILE_BARS[i % MOBILE_BARS.length];
                    return (
                        <div key={m.label}>
                            {i > 0 && <div className="my-9 h-[7px] w-full rounded-full bg-[#f9f9f9]" />}
                            <div className="flex flex-col gap-[6px]">
                                <p className="text-[64px] font-normal leading-[1.05] tracking-[-0.02em]">
                                    {m.dim && <span className="text-[#cfcfcf]">{m.dim}</span>}
                                    <CountUp end={m.end} decimals={m.decimals} suffix={m.suffix} />
                                </p>
                                <p className="text-[16px] leading-[24px]">{m.label}</p>
                            </div>

                            <div className="relative mt-4 h-[79px] w-full">
                                {/* Pill track */}
                                <div
                                    className="absolute inset-0 rounded-full opacity-[0.49]"
                                    style={{ backgroundImage: bar.background }}
                                />
                                {/* Light shimmer sweeping across the track */}
                                <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
                                    <span className="cg-shimmer" style={{ animationDelay: `${i * 0.6}s` }} />
                                </div>
                                {/* Grey track dot at the far left */}
                                <div className="absolute left-[14px] top-1/2 size-6 -translate-y-1/2 rounded-full bg-[#e0e0e0]">
                                    <div className="absolute left-1/2 top-1/2 size-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1b1b1b]" />
                                </div>
                                {/* White handle */}
                                <div
                                    className={`absolute top-1/2 size-[62px] -translate-y-1/2 rounded-full bg-white ${
                                        bar.handle === 'left' ? 'left-[10px]' : 'right-[10px]'
                                    }`}
                                    style={{ boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.04)' }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

/** The designed band holds three rows; the CMS caps figures at three too. */
export default function ImpactMetrics() {
    const rows = metrics().slice(0, DESKTOP_ROWS.length);
    if (!rows.length) return null;
    return (
        <>
            <div className="hidden lg:block">
                <ImpactMetricsDesktop rows={rows} />
            </div>
            <div className="lg:hidden">
                <ImpactMetricsMobile rows={rows} />
            </div>
        </>
    );
}
